import { createComponentLogger } from './logger';

import logger from '@/utils/logger';
const log = createComponentLogger('PerformanceTracker', 'metrics');

/**
 * Performance tracker for monitoring application startup and runtime metrics
 * Provides insights into startup time, bottlenecks, and performance regressions
 */
class StartupPerformanceTracker {
  private static instance: StartupPerformanceTracker;
  private metrics = new Map<string, number>();
  private startTime = performance.now();
  private reportGenerated = false;
  
  /**
   * Get singleton instance of the performance tracker
   */
  static getInstance(): StartupPerformanceTracker {
    if (!this.instance) {
      this.instance = new StartupPerformanceTracker();
    }
    return this.instance;
  }
  
  /**
   * Private constructor to enforce singleton pattern
   */
  private constructor() {
    // Mark app start time
    this.metrics.set('app_start', 0);
  }
  
  /**
   * Mark a performance milestone
   * @param label - The name of the milestone
   */
  mark(label: string): void {
    const elapsed = performance.now() - this.startTime;
    this.metrics.set(label, elapsed);
    
    // Log in development for immediate visibility
    if (import.meta.env.DEV) {
      log.debug(`Performance mark: ${label}`, { 
        elapsed: Math.round(elapsed),
        ms: `${Math.round(elapsed)}ms`
      });
    }
  }
  
  /**
   * Get the elapsed time for a specific mark
   * @param label - The mark to retrieve
   */
  getMark(label: string): number | undefined {
    return this.metrics.get(label);
  }
  
  /**
   * Calculate the duration between two marks
   * @param startMark - The starting mark
   * @param endMark - The ending mark
   */
  getDuration(startMark: string, endMark: string): number | undefined {
    const start = this.metrics.get(startMark);
    const end = this.metrics.get(endMark);
    
    if (start !== undefined && end !== undefined) {
      return end - start;
    }
    
    return undefined;
  }
  
  /**
   * Generate and log the performance report
   */
  async reportMetrics(): Promise<void> {
    // Prevent duplicate reports
    if (this.reportGenerated) {
      return;
    }
    this.reportGenerated = true;
    
    const totalStartup = performance.now() - this.startTime;
    
    // Build metrics object with calculated durations
    const metrics = {
      // Key milestones
      app_mount: this.metrics.get('app_mount') || 0,
      cleanup_complete: this.metrics.get('cleanup_complete') || 0,
      auth_init_start: this.metrics.get('auth_init_start') || 0,
      auth_init_complete: this.metrics.get('auth_init_complete') || 0,
      first_render: this.metrics.get('first_render') || 0,
      
      // Calculated durations
      cleanup_duration: this.getDuration('app_mount', 'cleanup_complete') || 0,
      auth_duration: this.getDuration('auth_init_start', 'auth_init_complete') || 0,
      
      // Total time
      total_startup: totalStartup
    };
    
    // Development console output - disabled for cleaner console
    if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_PERFORMANCE_LOGS === 'true') {
      // Format metrics for table display
      const tableData = Object.entries(metrics).map(([key, value]) => ({
        Metric: key.replace(/_/g, ' '),
        'Time (ms)': Math.round(value),
        Percentage: `${Math.round((value / totalStartup) * 100)}%`
      }));

      console.group('⚡ Startup Performance Report');
      console.table(tableData);

      // Only log critical performance issues
      if (totalStartup > 3000) {
        console.error(`⚠️ Critical: Slow startup ${Math.round(totalStartup)}ms`);
      }

      console.groupEnd();
      
      // Expose to window for debugging
      (window as any).__performanceMetrics = metrics;
    }
    
    // Log structured metrics for monitoring
    log.info('Startup performance metrics', {
      ...Object.fromEntries(
        Object.entries(metrics).map(([key, value]) => [key, Math.round(value)])
      ),
      performance: totalStartup < 500 ? 'excellent' : totalStartup < 1000 ? 'good' : totalStartup < 2000 ? 'acceptable' : 'slow'
    });
    
    // Could send to analytics service here
    // analytics.track('app_startup_performance', metrics);
  }
  
  /**
   * Reset the tracker (mainly for testing)
   */
  reset(): void {
    this.metrics.clear();
    this.startTime = performance.now();
    this.reportGenerated = false;
    this.metrics.set('app_start', 0);
  }
}

// Export singleton instance
export const performanceTracker = StartupPerformanceTracker.getInstance();